// admin-client/src/pages/Bookings.jsx
// =============================================================================
// Per-IPT booking import.
//
//   1. Pick the target month for this import session.
//   2. For each known IPT device, optionally attach a CrewLink .xlsx file.
//      (You don't have to fill every IPT — empty slots are skipped.)
//   3. Click "Upload" — every file goes into staging tagged with both the
//      month and the chosen device_id (so the parser's IPT column is
//      overridden).
//
//   4. The "By month" tab below groups uploads by their month label. Even when
//      separate IPT calendars for the same month land at different times,
//      they collapse into a single row with one "Preview" and one
//      "Apply month to live calendar" action.
//
//   5. Preview opens the temporary calendar (per-device hour grid). You can
//      review, then push the whole month live in one click.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { usePanScroll } from '../components/usePanScroll.js';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/datetime.js';
import { useAuth } from '../contexts/AuthContext.jsx';

const TODAY = new Date();
// Default to the PREVIOUS month: CrewLink calendars are only finalised
// after the month has rolled over, so the typical workflow is "import last
// month's data early in the new month".
const PREV = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
const DEFAULT_MONTH = `${PREV.getFullYear()}-${String(PREV.getMonth() + 1).padStart(2, '0')}`;

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysInMonth = (y, m1) => new Date(y, m1, 0).getDate();
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_COL_W = 86;
const HOUR_ROW_H = 44;

export default function Bookings() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const [devices, setDevices] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [month, setMonth] = useState(DEFAULT_MONTH);
  // perDeviceFile: { [device_id]: File | null }
  const [perDeviceFile, setPerDeviceFile] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);

  const refresh = async () => {
    try {
      const [d, u] = await Promise.all([api.devices.list(), api.uploads.list()]);
      setDevices(d.devices.filter((dv) => dv.status !== 'revoked'));
      setUploads(u.uploads);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const setFile = (deviceId, file) =>
    setPerDeviceFile((m) => ({ ...m, [deviceId]: file || null }));

  const upload = async () => {
    const entries = Object.entries(perDeviceFile).filter(([, f]) => !!f);
    if (entries.length === 0) {
      setErr('Pick at least one .xlsx file for an IPT.');
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setErr('Month must be YYYY-MM.');
      return;
    }
    setBusy(true); setErr(null); setInfo(null);
    try {
      const files = entries.map(([, f]) => f);
      const monthLabels = entries.map(() => month);
      const deviceIds = entries.map(([id]) => id);
      const r = await api.uploads.uploadWithDevices(files, monthLabels, deviceIds);
      const total = r.uploads.reduce((a, u) => a + u.rows_inserted, 0);
      const errs = r.uploads.reduce((a, u) => a + u.parse_errors, 0);
      setInfo(
        `${r.uploads.length} file(s) → ${total} staged rows${errs ? `, ${errs} parse errors` : ''}. Open the month below to preview and apply.`
      );
      setPerDeviceFile({});
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  // Build month-grouped view: { 'YYYY-MM': [upload, ...] }
  const grouped = useMemo(() => {
    const m = {};
    for (const u of uploads) {
      const key = u.month_label || '(unlabeled)';
      (m[key] = m[key] || []).push(u);
    }
    return m;
  }, [uploads]);
  const sortedMonths = Object.keys(grouped).sort().reverse();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">Bookings — monthly import</h1>
        <p className="text-sm text-slate-600">
          Upload a CrewLink Excel calendar per IPT. Pick the month once, attach one file per IPT
          (you can skip any), then click <strong>Upload to staging</strong>. The "By month" tab
          below groups every IPT's calendar for the same month into a single row — preview the
          combined temp calendar before applying it to the live calendar.
        </p>
      </header>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      {info && <div className="bg-green-50 text-green-800 px-3 py-2 rounded text-sm">{info}</div>}

      <section className="bg-white rounded shadow-sm border p-4 space-y-3">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-medium text-slate-800">1. Choose target month</h2>
          <label className="text-sm flex items-center gap-2">
            <span className="text-xs text-slate-600">Month (YYYY-MM)</span>
            <input
              pattern="\d{4}-\d{2}" required value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1.5 text-sm font-mono w-28"
            />
          </label>
        </header>
        <h2 className="font-medium text-slate-800 pt-2">2. Attach a file per IPT (skip any)</h2>
        {devices.length === 0 ? (
          <div className="text-sm text-slate-500">No IPT devices configured yet. Add devices on the <strong>Devices</strong> page first.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="text-slate-500 text-xs">
              <tr className="text-left">
                <th className="py-1 w-1/4">IPT</th>
                <th className="py-1">File</th>
                <th className="py-1">Size</th>
                <th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const f = perDeviceFile[d.id];
                return (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="py-1.5">
                      <div className="font-medium">{d.name}</div>
                      {d.location && <div className="text-xs text-slate-500">{d.location}</div>}
                    </td>
                    <td className="py-1.5">
                      <input
                        type="file" accept=".xlsx,.xls"
                        disabled={!canEdit}
                        onChange={(e) => setFile(d.id, e.target.files?.[0] || null)}
                        className="text-xs"
                      />
                    </td>
                    <td className="py-1.5 text-slate-500 text-xs">
                      {f ? `${Math.round(f.size / 1024)} KB` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-1.5 text-right">
                      {f && (
                        <button
                          onClick={() => setFile(d.id, null)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Clear
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {canEdit && (
          <button
            onClick={upload} disabled={busy || devices.length === 0}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            {busy
              ? 'Uploading…'
              : `Upload ${Object.values(perDeviceFile).filter(Boolean).length} file(s) to staging for ${month}`}
          </button>
        )}
      </section>

      <section className="bg-white rounded shadow-sm border overflow-hidden">
        <header className="px-4 py-2 border-b text-sm font-medium text-slate-800">
          3. Uploaded calendars — by month
        </header>
        {sortedMonths.length === 0 ? (
          <div className="px-4 py-4 text-center text-slate-500 text-sm">No uploads yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200 text-sm">
            {sortedMonths.map((mLabel) => {
              const list = grouped[mLabel];
              const stagedCount = list.filter((u) => u.staged).length;
              const totalRows = list.reduce((a, u) => a + (u.row_count_db || 0), 0);
              const open = openMonth === mLabel;
              return (
                <li key={mLabel}>
                  <button
                    onClick={() => setOpenMonth(open ? null : mLabel)}
                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{open ? '▾' : '▸'}</span>
                      <div>
                        <div className="font-mono text-base">{mLabel}</div>
                        <div className="text-xs text-slate-500">
                          {list.length} file(s) · {totalRows} rows
                          {stagedCount ? ` · ${stagedCount} staged (not yet live)` : ''}
                        </div>
                      </div>
                    </div>
                    <div>
                      {stagedCount > 0 ? (
                        <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded">staged</span>
                      ) : (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">all live</span>
                      )}
                    </div>
                  </button>
                  {open && (
                    <MonthRow
                      monthLabel={mLabel} list={list} canEdit={canEdit} onChanged={refresh}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

/* =============================================================================
   MonthRow — the expanded panel for one month: per-IPT preview + per-month
   apply-all action.
   ============================================================================= */
function MonthRow({ monthLabel, list, canEdit, onChanged }) {
  const [activeId, setActiveId] = useState(list[0]?.id || null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeId) { setDetail(null); return; }
    api.uploads.get(activeId).then(setDetail).catch((e) => setErr(e.message));
  }, [activeId]);

  // Committing an upload over live data is a choice the operator has to make
  // (replace the IPT's month outright, or merge slot-by-slot), so "Put to
  // Live" first asks the server what it would do. Only when nothing is live
  // at that scope do we commit straight through.
  const [plan, setPlan] = useState(null);

  const refreshAfterCommit = async (id) => {
    await onChanged();
    if (id === activeId) {
      const d = await api.uploads.get(id);
      setDetail(d);
    }
  };

  const applyOne = async (id) => {
    setBusy(true); setErr(null);
    try {
      const p = await api.uploads.commitPlan(id);
      if (!p.requires_choice) {
        await api.uploads.commit(id, { mode: 'merge' });
        await refreshAfterCommit(id);
      } else {
        setPlan(p); // hand off to the Replace/Merge dialog
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const runCommit = async (id, body) => {
    setBusy(true); setErr(null);
    try {
      await api.uploads.commit(id, body);
      setPlan(null);
      await refreshAfterCommit(id);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const applyAllStaged = async () => {
    const staged = list.filter((u) => u.staged);
    if (staged.length === 0) return;
    setBusy(true); setErr(null);
    try {
      // Commit everything that needs no decision; anything with live data at
      // its scope is surfaced one at a time rather than guessed at.
      const needsChoice = [];
      for (const u of staged) {
        const p = await api.uploads.commitPlan(u.id);
        if (p.requires_choice) { needsChoice.push(p); continue; }
        await api.uploads.commit(u.id, { mode: 'merge' });
      }
      await onChanged();
      if (activeId) {
        const d = await api.uploads.get(activeId);
        setDetail(d);
      }
      if (needsChoice.length > 0) {
        setErr(
          `${needsChoice.length} calendar(s) already have live data for that IPT and month — ` +
          `apply those individually to choose Replace or Merge.`
        );
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const discard = async (id) => {
    if (!confirm('Discard this calendar and its staged rows?')) return;
    setBusy(true); setErr(null);
    try {
      await api.uploads.delete(id);
      if (id === activeId) setActiveId(list.find((u) => u.id !== id)?.id || null);
      await onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-50/50 border-t border-slate-100 p-4 space-y-4">
      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {plan && (
        <CommitPlanDialog
          plan={plan}
          busy={busy}
          onCancel={() => setPlan(null)}
          onCommit={(body) => runCommit(plan.upload_id, body)}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-600">
          Calendars uploaded for <span className="font-mono">{monthLabel}</span> — pick one to preview.
        </div>
        {canEdit && list.some((u) => u.staged) && (
          <button
            onClick={applyAllStaged} disabled={busy}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-3 py-1.5 rounded text-xs"
          >
            Apply all staged ({list.filter((u) => u.staged).length}) to live calendar
          </button>
        )}
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {list.map((u) => (
          <li
            key={u.id}
            className={
              'border rounded p-2 text-xs cursor-pointer ' +
              (u.id === activeId ? 'border-cathay-400 bg-white shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50')
            }
            onClick={() => setActiveId(u.id)}
          >
            <div className="flex items-center justify-between">
              <div className="font-mono truncate" title={u.filename}>{u.filename}</div>
              <span className={
                'ml-2 text-[10px] px-1.5 py-0.5 rounded ' +
                (u.staged ? 'bg-amber-100 text-amber-800' : u.committed ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700')
              }>
                {u.staged ? 'staged' : u.committed ? 'live' : 'other'}
              </span>
            </div>
            <div className="text-slate-500 mt-0.5">
              {fmtDateTime(u.uploaded_at)} · {u.row_count_db} rows · by {u.uploaded_by_name}
            </div>
            {canEdit && (
              <div className="mt-1.5 flex gap-2 text-[11px]">
                {u.staged && (
                  <button onClick={(e) => { e.stopPropagation(); applyOne(u.id); }} className="text-green-700 hover:underline">
                    Apply
                  </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); discard(u.id); }} className="text-red-600 hover:underline">
                  {u.staged ? 'Discard' : 'Delete'}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {detail && <TempCalendarPreview detail={detail} monthLabel={monthLabel} />}
    </div>
  );
}

/* =============================================================================
   TempCalendarPreview — the same per-day-by-hour grid used to live in
   Bookings.UploadPreview. Now only one upload at a time.
   ============================================================================= */
/**
 * Replace-vs-Merge dialog, shown only when live scheduled data already exists
 * for the IPT + month an upload covers.
 *
 *   Replace — wipe that IPT's scheduled rows for the month, insert this file.
 *             Cancellations in the new file actually disappear.
 *   Merge   — identical slots stay, new slots are added, and every slot where
 *             the CathayID differs must be decided explicitly below.
 *
 * Crew iPad sign-ins are never touched by either path.
 */
function CommitPlanDialog({ plan, busy, onCancel, onCommit }) {
  const [mode, setMode] = useState(null); // null → choosing; 'replace' | 'merge'
  const [keep, setKeep] = useState({});   // `${date}|${hour}` → 'existing' | 'incoming'

  const conflicts = plan.conflicts || [];
  const allResolved = conflicts.every((c) => keep[`${c.booking_date}|${c.start_hour}`]);

  const commitMerge = () =>
    onCommit({
      mode: 'merge',
      resolutions: conflicts.map((c) => ({
        booking_date: c.booking_date,
        start_hour: c.start_hour,
        keep: keep[`${c.booking_date}|${c.start_hour}`],
      })),
    });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded shadow-lg border border-slate-200 w-full max-w-3xl my-8">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-medium text-slate-800">
            Live calendar data already exists
          </h3>
          <p className="text-xs text-slate-600 mt-1">
            <span className="font-mono">{plan.filename}</span> · {plan.month_label} — this IPT and
            month already has <strong>{plan.existing_bookings}</strong> booking(s)
            {plan.existing_events > 0 && <> and <strong>{plan.existing_events}</strong> event hour(s)</>}
            {' '}on the live calendar.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Crew iPad sign-ins are not affected by either option.
          </p>
        </div>

        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-center">
            <Stat label="Incoming" value={plan.staged_bookings} />
            <Stat label="Identical" value={plan.identical} />
            <Stat label="New slots" value={plan.additions} />
            <Stat label="Conflicts" value={conflicts.length} tone={conflicts.length ? 'amber' : null} />
          </div>

          {mode === null && (
            <div className="space-y-2 pt-2">
              <button
                disabled={busy}
                onClick={() => setMode('replace')}
                className="w-full text-left border border-red-200 hover:bg-red-50 rounded p-3"
              >
                <div className="font-medium text-red-700">Replace</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Delete this IPT's existing scheduled data for {plan.month_label} and insert the
                  new file. {plan.removals > 0 && (
                    <><strong>{plan.removals}</strong> live booking(s) not present in the new file
                    will be removed. </>
                  )}
                  The uploaded file becomes the source of truth.
                </div>
              </button>
              <button
                disabled={busy}
                onClick={() => setMode('merge')}
                className="w-full text-left border border-slate-300 hover:bg-slate-50 rounded p-3"
              >
                <div className="font-medium text-slate-800">Merge</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Keep identical entries, add the {plan.additions} new slot(s), and leave existing
                  bookings the file doesn't mention untouched.
                  {conflicts.length > 0
                    ? ` You'll choose a winner for each of the ${conflicts.length} conflicting slot(s).`
                    : ' No conflicts to resolve.'}
                </div>
              </button>
            </div>
          )}

          {mode === 'replace' && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-800">
              This deletes <strong>{plan.existing_bookings}</strong> live booking(s)
              {plan.existing_events > 0 && <> and <strong>{plan.existing_events}</strong> event hour(s)</>}
              {' '}for this IPT in {plan.month_label}, then inserts {plan.staged_bookings} from the
              file. Imported data can be re-uploaded; crew sign-ins are untouched.
            </div>
          )}

          {mode === 'merge' && conflicts.length > 0 && (
            <div className="border border-slate-200 rounded">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                Choose which booking to keep for each conflicting slot.
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {conflicts.map((c) => {
                  const k = `${c.booking_date}|${c.start_hour}`;
                  const hh = String(c.start_hour).padStart(2, '0');
                  return (
                    <div key={k} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <div className="w-32 shrink-0 font-mono text-slate-600">
                        {c.booking_date} {hh}:00
                      </div>
                      <ChoiceButton
                        active={keep[k] === 'existing'}
                        onClick={() => setKeep((s) => ({ ...s, [k]: 'existing' }))}
                        label="Keep existing"
                        value={c.existing.map((e) => e.cathay_id).join(', ')}
                      />
                      <ChoiceButton
                        active={keep[k] === 'incoming'}
                        onClick={() => setKeep((s) => ({ ...s, [k]: 'incoming' }))}
                        label="Use new"
                        value={c.incoming.cathay_id}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 flex gap-3">
                <button
                  className="underline"
                  onClick={() =>
                    setKeep(Object.fromEntries(conflicts.map((c) => [`${c.booking_date}|${c.start_hour}`, 'incoming'])))
                  }
                >
                  Use new for all
                </button>
                <button
                  className="underline"
                  onClick={() =>
                    setKeep(Object.fromEntries(conflicts.map((c) => [`${c.booking_date}|${c.start_hour}`, 'existing'])))
                  }
                >
                  Keep existing for all
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={mode === null ? onCancel : () => setMode(null)}
            disabled={busy}
            className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
          >
            {mode === null ? 'Cancel' : 'Back'}
          </button>
          {mode === 'replace' && (
            <button
              onClick={() => onCommit({ mode: 'replace' })}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Replacing…' : `Replace ${plan.month_label}`}
            </button>
          )}
          {mode === 'merge' && (
            <button
              onClick={commitMerge}
              disabled={busy || !allResolved}
              className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
              title={allResolved ? '' : 'Resolve every conflict first'}
            >
              {busy ? 'Merging…' : 'Merge'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={'rounded border p-2 ' + (tone === 'amber' ? 'border-amber-300 bg-amber-50' : 'border-slate-200')}>
      <div className="text-lg font-medium text-slate-800">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function ChoiceButton({ active, onClick, label, value }) {
  return (
    <button
      onClick={onClick}
      className={
        'flex-1 text-left rounded border px-2 py-1 ' +
        (active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 hover:bg-slate-50')
      }
    >
      <div className={'text-[10px] ' + (active ? 'text-slate-300' : 'text-slate-500')}>{label}</div>
      <div className="font-mono">{value}</div>
    </button>
  );
}

function TempCalendarPreview({ detail, monthLabel }) {
  const { upload, bookings } = detail;
  const pan = usePanScroll();
  const month = monthLabel || upload.month_label || (bookings[0]?.booking_date?.slice(0, 7));
  const [year, mon] = (month || `${TODAY.getFullYear()}-${String(TODAY.getMonth() + 1).padStart(2, '0')}`)
    .split('-').map(Number);
  const dim = daysInMonth(year, mon);

  const cellIndex = useMemo(() => {
    const idx = {};
    for (const b of bookings) {
      const base = new Date(b.booking_date);
      for (let h = b.start_hour; h < b.end_hour; h++) {
        const dayOffset = h >= 24 ? 1 : 0;
        const hour = h % 24;
        const d = new Date(base);
        d.setDate(base.getDate() + dayOffset);
        const k = `${ymd(d)}|${hour}`;
        (idx[k] = idx[k] || []).push(b);
      }
    }
    return idx;
  }, [bookings]);

  return (
    <section className="bg-white rounded shadow-sm border overflow-hidden">
      <header className="px-4 py-2 border-b flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-medium text-slate-800">
            Temp calendar — <span className="font-mono">{upload.filename}</span>
          </div>
          <div className="text-xs text-slate-500">
            Month {month || '—'} · {bookings.length} rows
            {upload.staged ? ' · staged (not yet applied)' : upload.committed ? ' · already live' : ''}
          </div>
        </div>
      </header>

      {upload.parse_errors?.length > 0 && (
        <div className="px-4 py-2 bg-red-50 text-red-800 text-xs">
          {upload.parse_errors.length} row(s) had parse errors and were skipped.
          {upload.parse_errors.slice(0, 3).map((e, i) => (
            <div key={i} className="font-mono mt-1">{e.sheet || ''} row {e.row || ''}: {e.error}</div>
          ))}
        </div>
      )}

      <div
        ref={pan.ref}
        onMouseDown={pan.onMouseDown}
        onContextMenu={pan.onContextMenu}
        title="Right-click and drag to pan the preview"
        className="p-3 overflow-auto h-[calc(100vh-18rem)] min-h-[18rem]"
      >
        <table className="text-[11px] border-collapse">
          <thead>
            <tr>
              <th
                rowSpan={2}
                className="sticky left-0 top-0 z-30 bg-slate-50 border-r border-slate-200 px-2 py-1.5 text-slate-600 text-left font-mono"
                style={{ width: 96, minWidth: 96 }}
              >
                Hour
              </th>
              {Array.from({ length: dim }, (_, i) => {
                const day = i + 1;
                const dt = new Date(year, mon - 1, day);
                const w = dt.getDay();
                return (
                  <th key={`wk-${day}`}
                    className={'sticky top-0 z-20 px-0 py-1 text-center border-l border-slate-100 ' +
                      ((w === 0 || w === 6) ? 'bg-slate-100 text-slate-600' : 'bg-slate-50 text-slate-500')}
                    style={{ width: DAY_COL_W, minWidth: DAY_COL_W }}>
                    {WEEKDAYS[w]}
                  </th>
                );
              })}
            </tr>
            <tr>
              {Array.from({ length: dim }, (_, i) => {
                const day = i + 1;
                return (
                  <th key={`d-${day}`}
                    className="sticky z-20 px-0 py-1 text-center border-l border-slate-100 bg-slate-50 text-slate-700 font-medium"
                    style={{ top: 28, width: DAY_COL_W, minWidth: DAY_COL_W }}>
                    {day}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 24 }, (_, h) => (
              <tr key={`h-${h}`} className="border-t border-slate-100">
                <th scope="row"
                  className="sticky left-0 z-10 bg-white border-r border-slate-200 px-2 py-1 text-left text-slate-600 font-mono whitespace-nowrap"
                  style={{ width: 96, minWidth: 96, height: HOUR_ROW_H }}>
                  {String(h).padStart(2, '0')}:00–{String((h + 1) % 24).padStart(2, '0')}:00
                </th>
                {Array.from({ length: dim }, (_, i) => {
                  const day = i + 1;
                  const dayDate = new Date(year, mon - 1, day);
                  const dayKey = ymd(dayDate);
                  const rows = cellIndex[`${dayKey}|${h}`] || [];
                  const cls = [
                    'border-l border-slate-100 align-middle text-center p-0.5',
                    rows.length > 0 ? 'bg-amber-100 border-l-amber-300 border-dashed' : '',
                  ].join(' ');
                  return (
                    <td key={`${day}-${h}`}
                      className={cls}
                      title={rows.map((b) => b.cathay_id).join(', ')}
                      style={{ width: DAY_COL_W, minWidth: DAY_COL_W, height: HOUR_ROW_H }}>
                      {rows.length > 0 && (
                        <span className="inline-block font-mono text-[10px] rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-amber-900 truncate">
                          {rows[0].cathay_id}{rows.length > 1 ? ` +${rows.length - 1}` : ''}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="border-t">
        <summary className="cursor-pointer px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          Raw row table ({bookings.length} rows)
        </summary>
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="min-w-full text-xs mt-2">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="py-1">Date</th>
                <th className="py-1">Hour</th>
                <th className="py-1">CathayID</th>
                <th className="py-1">Pilot</th>
              </tr>
            </thead>
            <tbody>
              {bookings.slice(0, 300).map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="py-1">{fmtDate(b.booking_date)}</td>
                  <td className="py-1">
                    {String(b.start_hour).padStart(2, '0')}:00 – {String(b.end_hour % 24).padStart(2, '0')}:00
                    {b.end_hour > 24 ? ' (+1d)' : ''}
                  </td>
                  <td className="py-1 font-mono">{b.cathay_id}</td>
                  <td className="py-1 text-slate-600">{b.pilot_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bookings.length > 300 && (
            <div className="text-xs text-slate-500 mt-2">… and {bookings.length - 300} more rows.</div>
          )}
        </div>
      </details>
    </section>
  );
}
